import {
  MATERIAL_TEMPLATE_SCHEMA,
  MATERIAL_TEMPLATE_TYPES,
  buildMaterialTemplateExportBundle,
  downloadJson,
  parseMaterialTemplateImportBundle,
  toPortableTemplateItem,
} from '@/shein-platform/utils/sheinMaterialTemplatePortable';
import { makeCustomSpecKey } from '@/shein-platform/utils/sheinSizeChart';
import { loadListingTemplateXLSX } from '@/utils/listingTemplateExcelUtils';

export const OFFICIAL_TEMPLATE_BATCH_MAX_ITEMS = 50;

export const PRODUCT_EXCEL_SHEET = '商品模板';
export const ATTRIBUTE_EXCEL_SHEET = '属性模板';
export const SIZE_CHART_EXCEL_SHEET = '尺码模板';

const PRODUCT_HEADERS = [
  '模板名称', '类目ID', '备注', '自动规则',
  '产品标题', '标题语言', '商品描述', '商家货号', '产地',
  '供货价', '库存', '克重', '包装长', '包装宽', '包装高',
];

const ATTRIBUTE_HEADERS = [
  '模板名称', '类目ID', '备注', '自动规则',
  '属性ID', '属性名', '属性值ID', '属性值', '成分比例',
];

const SIZE_HEADERS = [
  '模板名称', '类目ID', '备注', '自动规则',
  '规格', '尺寸属性ID', '尺寸属性名', '数值',
];

function asText(value) {
  if (value == null) return '';
  return String(value).trim();
}

function asBool(value, fallback = false) {
  const text = asText(value).toLowerCase();
  if (!text) return fallback;
  return text === '开' || text === '是' || text === 'true' || text === '1' || text === 'yes' || text === 'y';
}

function cell(row, key) {
  if (!row || typeof row !== 'object') return '';
  if (row[key] != null && asText(row[key])) return asText(row[key]);
  const hit = Object.keys(row).find((k) => asText(k) === key);
  return hit ? asText(row[hit]) : '';
}

function groupKey(name, categoryId) {
  return `${asText(name)}||${asText(categoryId)}`;
}

function assertItemLimit(items) {
  if (!Array.isArray(items) || !items.length) {
    throw new Error('文件中没有可导入的模板');
  }
  if (items.length > OFFICIAL_TEMPLATE_BATCH_MAX_ITEMS) {
    throw new Error(`单次最多导入 ${OFFICIAL_TEMPLATE_BATCH_MAX_ITEMS} 条，请拆分后重试`);
  }
}

export function emptyProductForm(overrides = {}) {
  return {
    title: '',
    titleLanguage: 'en',
    description: '',
    supplierCode: '',
    brandCode: '',
    originCountry: '',
    productAttributes: {},
    skcList: [],
    selectedSizeIds: [],
    printImages: [],
    sizeChart: {},
    supplyPrice: '',
    stockQty: '',
    weightGrams: '',
    weightUnit: 'g',
    pkgLength: '10',
    pkgWidth: '10',
    pkgHeight: '10',
    dimUnit: 'cm',
    selectedSites: [],
    mainSite: 'shein',
    shelfWay: '1',
    ...overrides,
  };
}

export function buildOfficialTemplateJsonSample(type) {
  if (type === MATERIAL_TEMPLATE_TYPES.productAttributes) {
    return buildMaterialTemplateExportBundle({
      type,
      shopId: 'official',
      items: [toPortableTemplateItem({
        name: '示例属性模板',
        remark: '',
        categoryId: '',
        productTypeId: '',
        categoryPathLabel: '',
        autoRuleEnabled: true,
        payload: {
          productAttributes: {
            '10001': '20001',
          },
        },
      }, type)],
    });
  }
  if (type === MATERIAL_TEMPLATE_TYPES.sizeCharts) {
    return buildMaterialTemplateExportBundle({
      type,
      shopId: 'official',
      items: [toPortableTemplateItem({
        name: '示例尺码模板',
        remark: '',
        categoryId: '',
        productTypeId: '',
        categoryPathLabel: '',
        autoRuleEnabled: true,
        sizeSaleAttributeId: '',
        payload: {
          sizeChart: {
            S: { '30001': '88' },
          },
          sizeRows: [{ id: 'S', name: 'S', custom: false }],
          measureAttrs: [{ attributeId: '30001', name: '胸围' }],
          measureAttrIds: ['30001'],
        },
      }, type)],
    });
  }
  return buildMaterialTemplateExportBundle({
    type: MATERIAL_TEMPLATE_TYPES.products,
    shopId: 'official',
    items: [toPortableTemplateItem({
      name: '示例商品模板',
      remark: '',
      categoryId: '',
      productTypeId: '',
      categoryPathLabel: '',
      autoRuleEnabled: true,
      payload: {
        form: emptyProductForm({
          title: 'Sample Title',
          originCountry: 'China',
        }),
        selectedAttrTemplateId: '',
        selectedSizeChartTemplateId: '',
      },
    }, MATERIAL_TEMPLATE_TYPES.products)],
  });
}

export function downloadOfficialTemplateJsonSample(type, filePrefix) {
  const bundle = buildOfficialTemplateJsonSample(type);
  downloadJson(bundle, `${filePrefix || '官方模板'}_批量样例.json`);
}

function excelSampleRows(type) {
  if (type === MATERIAL_TEMPLATE_TYPES.productAttributes) {
    return [{
      模板名称: '示例属性模板',
      类目ID: '',
      备注: '',
      自动规则: '开',
      属性ID: '10001',
      属性名: '领型',
      属性值ID: '20001',
      属性值: '',
      成分比例: '',
    }];
  }
  if (type === MATERIAL_TEMPLATE_TYPES.sizeCharts) {
    return [{
      模板名称: '示例尺码模板',
      类目ID: '',
      备注: '',
      自动规则: '开',
      规格: 'S',
      尺寸属性ID: '30001',
      尺寸属性名: '胸围',
      数值: '88',
    }];
  }
  return [{
    模板名称: '示例商品模板',
    类目ID: '',
    备注: '',
    自动规则: '开',
    产品标题: 'Sample Title',
    标题语言: 'en',
    商品描述: '',
    商家货号: '',
    产地: 'China',
    供货价: '',
    库存: '',
    克重: '',
    包装长: '10',
    包装宽: '10',
    包装高: '10',
  }];
}

function excelHeaders(type) {
  if (type === MATERIAL_TEMPLATE_TYPES.productAttributes) return ATTRIBUTE_HEADERS;
  if (type === MATERIAL_TEMPLATE_TYPES.sizeCharts) return SIZE_HEADERS;
  return PRODUCT_HEADERS;
}

function excelSheetName(type) {
  if (type === MATERIAL_TEMPLATE_TYPES.productAttributes) return ATTRIBUTE_EXCEL_SHEET;
  if (type === MATERIAL_TEMPLATE_TYPES.sizeCharts) return SIZE_CHART_EXCEL_SHEET;
  return PRODUCT_EXCEL_SHEET;
}

export async function downloadOfficialTemplateExcelSample(type, filePrefix) {
  const XLSX = await loadListingTemplateXLSX();
  const headers = excelHeaders(type);
  const rows = excelSampleRows(type);
  const aoa = [headers, ...rows.map((row) => headers.map((key) => row[key] ?? ''))];
  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, excelSheetName(type));
  XLSX.writeFile(workbook, `${filePrefix || '官方模板'}_批量样例.xlsx`);
}

function normalizeHeaderRow(row) {
  const out = {};
  Object.entries(row || {}).forEach(([key, value]) => {
    const header = asText(key);
    if (header) out[header] = value;
  });
  return out;
}

function isEmptyExcelRow(row, headers) {
  return headers.every((key) => !asText(row[key]));
}

function readExcelObjects(XLSX, workbook, sheetName, headers) {
  const name = (workbook.SheetNames || []).find((item) => asText(item) === sheetName)
    || workbook.SheetNames?.[0];
  if (!name) throw new Error('Excel 中没有工作表');
  const sheet = workbook.Sheets[name];
  const list = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return list
    .map(normalizeHeaderRow)
    .filter((row) => !isEmptyExcelRow(row, headers));
}

function parseProductExcelRows(rows) {
  const items = [];
  rows.forEach((row, index) => {
    const name = cell(row, '模板名称');
    if (!name) {
      throw new Error(`第 ${index + 2} 行缺少模板名称`);
    }
    const categoryId = cell(row, '类目ID');
    items.push({
      name,
      remark: cell(row, '备注'),
      categoryId,
      autoRuleEnabled: asBool(cell(row, '自动规则'), true),
      payload: {
        form: emptyProductForm({
          title: cell(row, '产品标题'),
          titleLanguage: cell(row, '标题语言') || 'en',
          description: cell(row, '商品描述'),
          supplierCode: cell(row, '商家货号'),
          originCountry: cell(row, '产地'),
          supplyPrice: cell(row, '供货价'),
          stockQty: cell(row, '库存'),
          weightGrams: cell(row, '克重'),
          pkgLength: cell(row, '包装长') || '10',
          pkgWidth: cell(row, '包装宽') || '10',
          pkgHeight: cell(row, '包装高') || '10',
        }),
        selectedAttrTemplateId: '',
        selectedSizeChartTemplateId: '',
      },
    });
  });
  return items;
}

function pushAttrValue(bucket, attrId, valueId, extraValue, percent) {
  const id = asText(attrId);
  if (!id) return;
  const vid = asText(valueId);
  const extra = asText(extraValue);
  const pct = asText(percent);
  if (pct) {
    const next = { valueId: vid || extra, percent: pct };
    const prev = bucket[id];
    if (Array.isArray(prev)) bucket[id] = [...prev, next];
    else if (prev) bucket[id] = [prev, next];
    else bucket[id] = [next];
    return;
  }
  if (vid && extra) {
    bucket[id] = extra;
    return;
  }
  const value = vid || extra;
  if (!value) return;
  const prev = bucket[id];
  if (prev == null || prev === '') {
    bucket[id] = value;
    return;
  }
  if (Array.isArray(prev)) {
    bucket[id] = [...prev, value];
    return;
  }
  bucket[id] = [prev, value];
}

function parseAttributeExcelRows(rows) {
  const grouped = new Map();
  rows.forEach((row, index) => {
    const name = cell(row, '模板名称');
    const attrId = cell(row, '属性ID');
    if (!name) throw new Error(`第 ${index + 2} 行缺少模板名称`);
    if (!attrId) throw new Error(`「${name}」第 ${index + 2} 行缺少属性ID`);
    const categoryId = cell(row, '类目ID');
    const key = groupKey(name, categoryId);
    if (!grouped.has(key)) {
      grouped.set(key, {
        name,
        remark: cell(row, '备注'),
        categoryId,
        autoRuleEnabled: asBool(cell(row, '自动规则'), true),
        productAttributes: {},
      });
    }
    const target = grouped.get(key);
    pushAttrValue(
      target.productAttributes,
      attrId,
      cell(row, '属性值ID'),
      cell(row, '属性值'),
      cell(row, '成分比例'),
    );
  });
  return [...grouped.values()].map((row) => ({
    name: row.name,
    remark: row.remark,
    categoryId: row.categoryId,
    autoRuleEnabled: row.autoRuleEnabled,
    payload: {
      productAttributes: row.productAttributes,
      categoryId: row.categoryId,
    },
  }));
}

function parseSizeChartExcelRows(rows) {
  const grouped = new Map();
  rows.forEach((row, index) => {
    const name = cell(row, '模板名称');
    const spec = cell(row, '规格');
    if (!name) throw new Error(`第 ${index + 2} 行缺少模板名称`);
    if (!spec) throw new Error(`「${name}」第 ${index + 2} 行缺少规格`);
    const categoryId = cell(row, '类目ID');
    const key = groupKey(name, categoryId);
    if (!grouped.has(key)) {
      grouped.set(key, {
        name,
        remark: cell(row, '备注'),
        categoryId,
        autoRuleEnabled: asBool(cell(row, '自动规则'), true),
        sizeChart: {},
        sizeRows: [],
        measureAttrs: [],
        measureSeen: new Set(),
        sizeSeen: new Set(),
      });
    }
    const target = grouped.get(key);
    const specKey = makeCustomSpecKey(spec);
    if (!target.sizeSeen.has(specKey)) {
      target.sizeSeen.add(specKey);
      target.sizeRows.push({ id: specKey, name: spec, custom: true });
      target.sizeChart[specKey] = {};
    }
    const attrId = cell(row, '尺寸属性ID') || cell(row, '尺寸属性名');
    const attrName = cell(row, '尺寸属性名') || attrId;
    const value = cell(row, '数值');
    if (attrId && value) {
      target.sizeChart[specKey][attrId] = value;
      if (!target.measureSeen.has(attrId)) {
        target.measureSeen.add(attrId);
        target.measureAttrs.push({ attributeId: attrId, name: attrName });
      }
    }
  });
  return [...grouped.values()].map((row) => ({
    name: row.name,
    remark: row.remark,
    categoryId: row.categoryId,
    autoRuleEnabled: row.autoRuleEnabled,
    payload: {
      sizeChart: row.sizeChart,
      sizeRows: row.sizeRows,
      measureAttrs: row.measureAttrs,
      measureAttrIds: row.measureAttrs.map((attr) => attr.attributeId),
      categoryId: row.categoryId,
    },
  }));
}

export function parseOfficialTemplateExcelObjects(type, rows) {
  const list = Array.isArray(rows) ? rows : [];
  let items;
  if (type === MATERIAL_TEMPLATE_TYPES.productAttributes) {
    items = parseAttributeExcelRows(list);
  } else if (type === MATERIAL_TEMPLATE_TYPES.sizeCharts) {
    items = parseSizeChartExcelRows(list);
  } else {
    items = parseProductExcelRows(list);
  }
  assertItemLimit(items);
  return items;
}

export async function parseOfficialTemplateExcelFile(type, file) {
  const { parseOfficialTemplateExcelWorkbook } = await import('@/shein-platform/utils/sheinOfficialTemplateCategoryBatch');
  return parseOfficialTemplateExcelWorkbook(type, file);
}

export function parseOfficialTemplateJsonData(type, data) {
  const bundle = parseMaterialTemplateImportBundle(data, type);
  assertItemLimit(bundle.items);
  return bundle.items;
}

export function portableItemsToCreateBodies(type, items = []) {
  return (Array.isArray(items) ? items : []).map((item) => {
    const body = {
      name: item.name,
      remark: item.remark || undefined,
      productTypeId: item.productTypeId || undefined,
      categoryId: item.categoryId || undefined,
      categoryPathLabel: item.categoryPathLabel || undefined,
      autoRuleEnabled: !!item.autoRuleEnabled,
      payload: item.payload && typeof item.payload === 'object' ? item.payload : {},
    };
    if (type === MATERIAL_TEMPLATE_TYPES.sizeCharts && item.sizeSaleAttributeId) {
      body.sizeSaleAttributeId = item.sizeSaleAttributeId;
    }
    return body;
  });
}

export const OFFICIAL_TEMPLATE_BATCH_ACCEPT = '.json,.xlsx,.xls,.zip,application/json,application/zip,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function detectOfficialTemplateBatchKind(file) {
  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.zip')) return 'zip';
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'xlsx';
  if (name.endsWith('.json')) return 'json';
  return '';
}

export async function readOfficialTemplateBatchFile(file) {
  if (!file) throw new Error('未选择文件');
  const kind = detectOfficialTemplateBatchKind(file);
  if (!kind) throw new Error('请上传 JSON、Excel 或 zip 文件');
  if (kind === 'json') {
    const text = await file.text();
    return { file, kind, text, data: JSON.parse(text) };
  }
  return { file, kind };
}

/** 弹出文件选择：JSON 或 Excel */
export function pickOfficialTemplateBatchFile() {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = OFFICIAL_TEMPLATE_BATCH_ACCEPT;
    input.style.display = 'none';
    let settled = false;
    const cleanup = () => {
      window.removeEventListener('focus', onWindowFocus);
      if (input.parentNode) input.parentNode.removeChild(input);
    };
    const fail = (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err || '未选择文件')));
    };
    const succeed = (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    };
    const onWindowFocus = () => {
      window.setTimeout(() => {
        if (!settled && !input.files?.length) {
          fail(new Error('未选择文件'));
        }
      }, 300);
    };
    input.onchange = async () => {
      try {
        succeed(await readOfficialTemplateBatchFile(input.files?.[0]));
      } catch (e) {
        fail(e);
      }
    };
    document.body.appendChild(input);
    window.addEventListener('focus', onWindowFocus);
    input.click();
  });
}
